import { SelectProps } from "@tfg/ui"
import { chain, omit, uniqBy } from "lodash"
import { useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"

import {
  FindCertificatesByOrganizationIdResponseDto,
  useFindCertificatesByOrganizationId,
} from "~/gen/api"
import { CertificatesTable } from "~/pages/Brand/Certificates/components/CertificatesTable"
import { CertificatesListView } from "~/pages/Brand/Certificates/constants"
import { useSearchParamsFilters } from "~/pages/Brand/Certificates/hooks/useSearchParamsFilters"
import {
  CertificatesTableContext,
  defaultContext,
} from "~/pages/Brand/Certificates/provider/CertificatesTableContext"

export const CertificatesTableProvider = () => {
  const [globalFilter, setGlobalFilter] = useState("")

  const [certificateTypes, setCertificateTypes] = useState<SelectProps["data"]>(
    []
  )

  const [checkedCertificates, setCheckedCertificates] = useState<Set<string>>(
    new Set()
  )

  const [selectedCertificateInfo, setSelectedCertificateInfo] =
    useState<FindCertificatesByOrganizationIdResponseDto | null>(null)

  const params = useParams()

  const view = (params.view ??
    CertificatesListView.SCOPE) as CertificatesListView

  const viewRef = useRef(view)

  const [filters, setFilters, unsetFilter] = useSearchParamsFilters()

  const {
    data: certificates,
    isLoading,
    isError,
    error,
  } = useFindCertificatesByOrganizationId({
    ...filters,
    isTransaction: view === CertificatesListView.TRANSACTION,
  })

  const fetchedCertificateTypes = useMemo<SelectProps["data"]>(() => {
    return uniqBy(
      certificates?.data.flatMap((certificate) =>
        certificate.type
          ? {
              value: certificate.type.id.toString(),
              label: certificate.type.name,
            }
          : []
      ) ?? [],
      "value"
    )
  }, [certificates])

  useEffect(() => {
    setCertificateTypes((prev) => {
      return chain(fetchedCertificateTypes)
        .concat(prev || [])
        .uniqBy("value")
        .value()
    })
  }, [fetchedCertificateTypes, setCertificateTypes])

  useEffect(() => {
    if (viewRef.current !== view) {
      setFilters((prev) => omit(prev, "certificateTypeId"))
      setCertificateTypes(fetchedCertificateTypes)
    }

    viewRef.current = view
  }, [view, fetchedCertificateTypes, setFilters])

  return (
    <CertificatesTableContext.Provider
      value={{
        ...defaultContext,
        globalFilter,
        view,
        filters,
        certificates: certificates?.data ?? [],
        isError,
        error,
        isLoading,
        checkedCertificates,
        selectedCertificateInfo,
        certificateTypes,
        setCertificateTypes,
        setCheckedCertificates,
        setSelectedCertificateInfo,
        setGlobalFilter,
        setFilters,
        unsetFilter,
      }}
    >
      <CertificatesTable />
    </CertificatesTableContext.Provider>
  )
}
